import type { JoinRequestBody } from '../api/chat-api.js';
import { PermissionFlags } from '../contracts/chat-permissions.js';
import type {
  APIChannel,
  APIInvite,
  APIMemberVerification,
  APIWelcomeScreen,
} from '../contracts/chat-types.js';
import { can } from '../permissoes/index.js';
import type { ChatState, Snowflake } from '../stores/state.js';

/**
 * Chegada a um servidor (web e celular), como no Discord: a tela de
 * boas-vindas ("Welcome Screen") e o aceite das regras ("Membership
 * Screening"). Quem entra num servidor que pede o aceite fica pendente: lê
 * os canais, mas não escreve nem reage até aceitar.
 */

/** O que aparece depois de entrar por convite. */
export type PassoDaEntrada = 'boas-vindas' | 'regras';

/**
 * Na ordem do Discord: primeiro a tela de boas-vindas (se o servidor tiver),
 * depois as regras (se pedir o aceite). Quem já era membro não vê nenhuma.
 */
export function passosDaEntrada(
  convite: Pick<APIInvite, 'guild' | 'new_member' | 'show_verification_form'>,
): PassoDaEntrada[] {
  if (!convite.new_member) return [];
  const passos: PassoDaEntrada[] = [];
  if (convite.guild.features.includes('WELCOME_SCREEN_ENABLED')) {
    passos.push('boas-vindas');
  }
  if (convite.show_verification_form) passos.push('regras');
  return passos;
}

/** Ainda não aceitou as regras do servidor. */
export function regrasPendentes(
  estado: ChatState,
  guildId: Snowflake,
): boolean {
  const eu = estado.me?.id;
  return !!eu && !!estado.members[guildId]?.[eu]?.pending;
}

/** As regras do servidor, na ordem (as de todos os termos do formulário). */
export function regrasDoServidor(verificacao: APIMemberVerification): string[] {
  return verificacao.form_fields
    .filter((campo) => campo.field_type === 'TERMS')
    .flatMap((campo) => campo.values)
    .map((regra) => regra.trim())
    .filter(Boolean);
}

/**
 * A caixa de concordar: o texto do formulário (nulo quando o servidor não
 * escreveu regras; aí basta aceitar) e se ela precisa estar marcada.
 */
export function caixaDoAceite(
  verificacao: APIMemberVerification,
): { rotulo: string; obrigatoria: boolean } | null {
  const termos = verificacao.form_fields.filter(
    (campo) => campo.field_type === 'TERMS',
  );
  if (!termos.length) return null;
  return {
    rotulo: termos[0].label.trim() || 'Li e concordo com as regras',
    obrigatoria: termos.some((campo) => campo.required),
  };
}

/** `PUT /guilds/:id/requests/@me`: cada termo do formulário com o "concordo". */
export function corpoDoAceite(
  verificacao: APIMemberVerification,
): JoinRequestBody {
  return {
    ...(verificacao.version ? { version: verificacao.version } : {}),
    form_fields: verificacao.form_fields.map((campo) => ({
      ...campo,
      response: true,
    })),
  };
}

export type CanalDasBoasVindas = {
  canal: APIChannel;
  descricao: string;
  /** Emoji do servidor (`id`) ou Unicode (`name`). */
  emoji: { id: Snowflake | null; name: string | null } | null;
};

/**
 * Os canais da tela de boas-vindas (até 5, na ordem de quem configurou),
 * só os que a pessoa vê, como no Discord.
 */
export function canaisDasBoasVindas(
  estado: ChatState,
  tela: APIWelcomeScreen,
): CanalDasBoasVindas[] {
  return tela.welcome_channels.flatMap((item) => {
    const canal = estado.channels[item.channel_id];
    if (!canal || !can(estado, canal.id, PermissionFlags.VIEW_CHANNEL)) {
      return [];
    }
    return [
      {
        canal,
        descricao: item.description,
        emoji:
          item.emoji_id || item.emoji_name
            ? { id: item.emoji_id, name: item.emoji_name }
            : null,
      },
    ];
  });
}
